import React, { useMemo, useCallback, ReactNode } from "react";
import { QueryLoader, Text, Button, toast, Flex } from "@jsq/ds/core";
import {
  useGetProfilesForCaseQuery,
  useSubscriptionSubmitIdentificationKycMutation,
} from "../../graphql/supergraph.index";
import { clientContext } from "../../utils/graphql_utils";
import { textColorNeutral800 } from "../common/style_vanilla.css";
import { TreeComponent } from "../common/TreeComponent";
import { Node } from "../common/TreeComponent/types";
import { ProfileTreeCard } from "./ProfileTreeCard";
import {
  completeButtonContainer,
  edgeLabel,
  rootEdgeLabel,
  stepDescription,
} from "./style_vanilla.css";
import { ProfileTreeNode, ProfileTreeProps, ProfileType } from "./types";
import { buildHierarchy, extractChildProfiles, provideLegalName } from "./utils";

/**
 * A hierarchical tree visualization component that displays ownership relationships
 * between entities in a compliance case. The component fetches profile data from GraphQL,
 * processes it into a hierarchical structure, and renders it as an interactive tree.
 *
 * The tree represents the ownership hierarchy of an organization, showing:
 */
export const ProfileTree: React.FC<ProfileTreeProps> = ({
  caseId,
  basePath,
  arenaId,
  subscriptionId,
  onStepCompletion,
}) => {
  const profileResults = useGetProfilesForCaseQuery({
    context: clientContext(),
    fetchPolicy: "no-cache",
    variables: {
      caseId: parseInt(caseId),
    },
  });

  const [subscriptionSubmitIdentificationKycMutation, { loading: isSubmitting }] =
    useSubscriptionSubmitIdentificationKycMutation();

  const handleCompleteIdentification = useCallback(async () => {
    try {
      await subscriptionSubmitIdentificationKycMutation({
        variables: {
          subscriptionId,
          arenaId,
        },
      });

      onStepCompletion();
    } catch (error) {
      toast.error("Error submitting identification");
    }
  }, [subscriptionSubmitIdentificationKycMutation, subscriptionId, arenaId, onStepCompletion]);

  /**
   * Creates edge labels showing ownership percentages
   *
   * This function generates a label to display on the connecting edge between
   * parent and child nodes, showing the ownership percentage when available.
   *
   * @param percentOwnership - Optional percentage of ownership to display
   * @returns React component for the edge label or null if no percentage exists
   */
  const provideEdgeLabel = useCallback(
    (percentOwnership?: number | null, className: string = edgeLabel): ReactNode => {
      return <Text variant="strong-sm" className={className}>{`${percentOwnership}%`}</Text>;
    },
    [],
  );

  /**
   * Memoized extraction of child profiles with parent relationships
   *
   * This processes the raw GraphQL data to:
   * 1. Filter out null profiles from the array
   * 2. Extract only profiles that have relevant parent relationships
   *
   * The result is a clean array of profiles that will be used to build the hierarchy.
   */
  const allChildProfiles = useMemo(
    () =>
      extractChildProfiles(
        profileResults.data?.complianceCase?.profiles?.filter(
          (profile): profile is ProfileType => profile !== null,
        ) || [],
      ),
    [profileResults],
  );

  /**
   * Memoized extraction of the main investing profile
   *
   * This will be used as the root node of the ownership tree.
   */
  const investingProfile = useMemo(
    () => profileResults.data?.complianceCase?.investingProfile,
    [profileResults],
  );

  /**
   * Transforms a hierarchical profile node into the Tree component's Node format
   *
   * This recursive function processes each node in the hierarchy and:
   * 1. Converts IDs to strings (required by the TreeComponent)
   * 2. Generates the visual content for the node
   * 3. Adds edge labels with ownership percentages
   * 4. Recursively processes child nodes
   *
   * @param node - The hierarchical profile node to transform
   * @returns Node structure compatible with the TreeComponent or null for invalid nodes
   */
  const stripProfileNode = useCallback(
    (node: ProfileTreeNode | null): Node | null => {
      if (!node) return null;

      return {
        id: node.id.toString(),
        content: <ProfileTreeCard profileInfo={node.profileInfo} basePath={basePath} />,
        parentId: node.parentId?.toString(),
        edgeLabel: node.percentageOwnership && provideEdgeLabel(node.percentageOwnership),
        rootEdgeLabel:
          node.rootPercentageOwnership &&
          provideEdgeLabel(node.rootPercentageOwnership, rootEdgeLabel),
        leafNodes: Array.isArray(node.children)
          ? node.children
              .map((child) => stripProfileNode(child))
              .filter((child): child is Node => child !== null)
          : [],
      };
    },
    [provideEdgeLabel, basePath],
  );

  /**
   * Constructs the complete hierarchy data for display
   *
   * This combines all the processed data to:
   * 1. Build the hierarchical structure using the profile data
   * 2. Transform it into the format required by the TreeComponent
   *
   * Returns null if the investing profile is missing, which will trigger error handling.
   */
  const displayData = useMemo(() => {
    if (!investingProfile) return null;
    const hierarchy = buildHierarchy(allChildProfiles, investingProfile);
    return hierarchy ? stripProfileNode(hierarchy) : null;
  }, [allChildProfiles, investingProfile, stripProfileNode]);

  /**
   * Gets the organization name to display in the header
   *
   * Uses the legal name of the investing profile or a placeholder if not available.
   */
  const orgName = useMemo(() => provideLegalName(investingProfile), [investingProfile]);
  const allProfilesCompleted = useMemo(() => {
    return profileResults.data?.complianceCase?.profiles?.every((profile) => {
      return profile?.parentRelationships?.every(
        (pr) =>
          (profile?.complianceObjectType === "institution" &&
            (pr?.relationshipType !== "owner" ||
              profile?.caseDataEntryStatus?.ownersCompleted) &&
            (pr?.relationshipType !== "controller" ||
              profile?.caseDataEntryStatus?.controllersCompleted)) ||
          (profile?.complianceObjectType === "individual" && profile?.dateOfBirth),
      );
    });
  }, [profileResults]);

  return (
    <QueryLoader result={profileResults}>
      <Flex flexDirection="column">
        <Text variant="h3">{`${orgName} organization structure`}</Text>
        <Text variant="regular-md" className={stepDescription}>
          {`Overview of owners and controllers of ${orgName} and its subsidiaries.`}
        </Text>
        <div role="tree">
          {displayData ? (
            <TreeComponent data={displayData} rootEdgeStyle={{ dashWidth: 3, dashGap: 3 }} />
          ) : (
            <Text variant="regular-md" className={textColorNeutral800}>
              No ownership structure data is available for this organization.
            </Text>
          )}
        </div>
        <div className={completeButtonContainer}>
          <Button
            variant="primary"
            onClick={() => {
              void handleCompleteIdentification();
            }}
            isLoading={isSubmitting}
            data-testid="profile-tree-complete-button"
            disabled={!allProfilesCompleted}
          >
            Complete Identification
          </Button>
        </div>
      </Flex>
    </QueryLoader>
  );
};
